import { useAcceptInviteMutation } from '@/organizations/apis/org-admin.api'
import { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '@/context/auth-context'
import signOutOfApp from '@/firebase/auth/sign-out'
import { useHubspotConversations } from '@/context/useHubspotConversations'
import { getBrandFromHost, getLogoFromBrand } from '@/util/enterprise'
import { BrandEnum } from '@/firebase/auth/auth-jwt-schema'
import FullPageLoader from '@/components/loaders/FullPageLoader'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType } from '@/constants/constants-ui'
import { useAppDispatch } from '@/store/store-hooks'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getCurrentUserSampleMatterId } from '@/matter/utils/get-current-user-sample-matter-id'
import { Userpilot } from 'userpilot'

const hostBrand = getBrandFromHost()
const brandLogoUrl = getLogoFromBrand(hostBrand)

type InviteAcceptProps = {
  inviteId: string
  brand?: string
}

type InviteAcceptStatus = 'idle' | 'accepting' | 'waiting-for-org' | 'completed' | 'error'

export default function InviteAccept(props: InviteAcceptProps) {
  const { inviteId, brand } = props
  const { user, userAccountData, refreshUserAccountData } = useContext(AuthContext)
  const navigate = useNavigate()
  const { toggleWidget } = useHubspotConversations()
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  // Inferred values
  const email = user?.email
  const isOrgAccount = userAccountData?.isOrgAccount ?? false
  const orgName = userAccountData?.orgData?.name

  // Local state
  const [error, setError] = useState<string | null>(null)

  const inviteAcceptStatus = useRef<InviteAcceptStatus>('idle')

  // RTK-Query Mutation
  const [acceptInvite] = useAcceptInviteMutation()

  // ======================================================================
  // POLLING NOTE
  // The pending payment poll won't work
  // for existing users who are already paid or trialed
  // who are accepting an invite. So we have a dedicated
  // poll here.
  // ======================================================================

  async function handleAccept() {
    if (inviteAcceptStatus.current === 'accepting') return

    console.log('Handling invitation acceptance...')
    inviteAcceptStatus.current = 'accepting'

    try {
      setError(null)
      await acceptInvite({ invite_id: inviteId, brand }).unwrap()
      trackEvent(AnalyticsEvent.OrganizationInviteAccepted)
      inviteAcceptStatus.current = 'waiting-for-org'
    } catch (e: unknown) {
      inviteAcceptStatus.current = 'error'
      const status = typeof e === 'object' && e != null && 'status' in e ? e.status : undefined

      if (status === 404) {
        setError('404: The invitation could not be found. Try again or contact your organization admin.')
      } else if (status === 410) {
        setError('Your invitation was already completed. Please contact your organization admin.')
      } else {
        setError('There was an error accepting your invitation.')
      }
    }

    // Loading status is intended to continue until polling finds the user in an org
  }

  // Poll for invite status
  // Polls until the user's JWT is updated with the organization_id
  // Then triggers an AuthContext refresh with the latest user data
  useEffect(() => {
    const pollInterval = setInterval(() => {
      console.log('Polling for invite status...')
      // Refresh user account data
      refreshUserAccountData()
      // poll for invite status every 3 seconds
    }, 3000)

    // automatically clear the interval when the component unmounts
    return () => {
      clearInterval(pollInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // On mount, automatically accept the invite if not already in an organization
  useEffect(() => {
    // only run once, and only when the userAccountData and inviteId have loaded in
    if (inviteAcceptStatus.current === 'idle' && userAccountData && inviteId) {
      // If the user is already in an organization, do not accept the invite, display an error toast and navigate to the home page
      if (isOrgAccount) {
        console.log('User is already in an organization.')
        inviteAcceptStatus.current = 'completed'
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: `You're already a member of ${orgName}.` }))
        navigate('/')
      } else {
        console.log('User is not in an organization. Accepting invite...')
        handleAccept()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteId, userAccountData])

  // When poll detects the user is in an organization, display a success toast and navigate to the home page
  useEffect(() => {
    if (inviteAcceptStatus.current === 'waiting-for-org' && isOrgAccount) {
      console.log('User has joined the organization. Navigating to home page...')
      inviteAcceptStatus.current = 'completed'

      async function completeInvite() {
        if (user?.uid) {
          try {
            const sampleMatterId = await getCurrentUserSampleMatterId()

            if (sampleMatterId) {
              Userpilot.identify(user.uid, { sampleMatterId })
              Userpilot.reload()
            } else {
              console.warn('Userpilot sample matter id missing after organization invite acceptance')
            }
          } catch (error) {
            console.error(`Userpilot sample matter id lookup failed after organization invite acceptance for uid ${user.uid}:`, error)
          }
        } else {
          console.error('Userpilot sample matter id sync skipped after organization invite acceptance because user id was missing')
        }

        dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Joined ${orgName}.` }))
        navigate('/')
      }

      completeInvite()
    }
  }, [dispatch, isOrgAccount, navigate, orgName, user?.uid])

  if (!error) {
    return <FullPageLoader />
  }

  return (
    <div className={'grow flex flex-col justify-center items-center min-h-screen py-5'}>
      <img className="mx-auto w-[200px] mb-4" src={brandLogoUrl} alt={hostBrand} />
      <div className="rounded-lg bg-brand-neutral-50 px-4 pb-4 pt-5 mb-5 text-left shadow-xl transition-all sm:w-full sm:max-w-md sm:p-6">
        {(email || orgName) && (
          <div className={'mb-2  text-center'}>
            <span className="font-semibold">{email}</span>
          </div>
        )}
        <div className={'flex flex-col items-center gap-x-2 justify-center'}>
          <div className={'mb-4 text-red-700 text-center'}>{error}</div>
          <button
            className={
              'rounded-md bg-brand-500 border-[1px] border-brand-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400'
            }
            onClick={handleAccept}
          >
            Retry
          </button>
        </div>
      </div>
      <div className={'flex gap-5'}>
        <button
          onClick={() => {
            signOutOfApp(navigate)
          }}
          className="font-semibold leading-6 text-brand-500 hover:text-brand-400"
        >
          Sign out
        </button>
        {hostBrand != BrandEnum.HAIKU && (
          <button
            onClick={() => {
              toggleWidget()
            }}
            className="font-semibold leading-6 text-brand-500 hover:text-brand-400"
          >
            Chat with support
          </button>
        )}
      </div>
    </div>
  )
}
